import {action, comparer, makeObservable, observable, reaction, runInAction} from "mobx";
import {
	accountUuid,
	annotation,
	annotationStub,
	annotationUpdates,
	annotationUuid,
	outlineTopic,
	projectUuid,
	syncData,
	syncHandler,
	topicUuid
} from "../model/index.js";
import app from "../app.js";
import outlineStore from "./outline.js";
import projectStore from "./project.js";
import {cleanUp, getDateString, loadObject, prepareForCreate, prepareForUpdate} from "../utils/index.js";
import {outSyncHandler, Sync} from "../sync/index.js";
import {Store} from "./index.js";
import {fetchLockOptions} from "./common.js";
import Uuid from "../../utils/uuid.js";
import AsyncLock from "async-lock";

/**
 * We store annotations in a 2-level map, by project UUID.
 */
type dataModel = Record<projectUuid, Record<annotationUuid, annotation>>;

export class AnnotationStore implements Store {
	_annotations: dataModel = {};
	_fetchLock = new AsyncLock(fetchLockOptions);
	id:           number;

	constructor() {
		makeObservable(this, {
			create:                action,
			update:                action,
			delete:                action,
			moveToTopic:           action,
			_annotations:          observable,
			_clear:                action,
			_load:                 action,
			_loadMultiple:         action,
			_loadUpdates:          action,
			_remove:               action,
			_loadFromSync:         action,
			_loadMultipleFromSync: action
		});
	}

	_clear() {
		this._annotations = {};
	}

	_refresh() {
		(Object.keys(this._annotations) as projectUuid[])
			.forEach(uuid => this._fetchByProject(uuid, true));
	}

	_isProjectLoaded(projectUuid: projectUuid): boolean {
		return this._annotations[projectUuid] !== undefined;
	}

	byProject(projectUuid: projectUuid): annotation[] {
		if (!this._isProjectLoaded(projectUuid)) {
			// noinspection JSIgnoredPromiseFromCall - we don't wait for this to return
			this._fetchByProject(projectUuid);
		}

		return Object.values(this._annotations[projectUuid] ?? {});
	}

	/**
	 * Returns an annotation by its UUID. Does not trigger project loading (as it doesn't know the project UUID).
	 */
	byUuid(uuid: annotationUuid): annotation | undefined {
		for (const projectAnnotations of Object.values(this._annotations)) {
			if (projectAnnotations[uuid])
				return projectAnnotations[uuid];
		}
		return undefined;
	}

	async _fetchByProject(projectUuid: projectUuid, force = false) {
		await this._fetchLock.acquire("_fetchByProject:" + projectUuid, async () => {
			if (!app.isLoggedIn)
				return;
			if (force || !this._isProjectLoaded(projectUuid)) {
				const annotations = await app.backend.getAnnotations(projectUuid);
				runInAction(() => {
					if (force && this._isProjectLoaded(projectUuid)) {
						// Delete all the annotations loaded for the project first (used by `this.refresh()`).
						(Object.keys(this._annotations[projectUuid]) as annotationUuid[])
							.forEach(annotationUuid => delete this._annotations[projectUuid][annotationUuid]);
					}

					this._loadMultiple(projectUuid, annotations);
				});
			}
		});
	}

	_load(projectUuid: projectUuid, annotation: annotation) {
		if (this._annotations[projectUuid] === undefined)
			this._annotations[projectUuid] = {};
		loadObject(this._annotations[projectUuid], annotation.uuid, annotation);
	}

	_loadMultiple(projectUuid: projectUuid, annotations: annotation[]) {
		// We need this for _isProjectLoaded to work when there are no annotations in the project
		if (this._annotations[projectUuid] === undefined)
			this._annotations[projectUuid] = {};

		annotations.map(this._load.bind(this, projectUuid));
	}

	_loadUpdates(uuid: annotationUuid, projectUuid: projectUuid, updates: annotationUpdates) {
		if (!this._isProjectLoaded(projectUuid) || this._annotations[projectUuid][uuid] === undefined)
			return;

		Object.assign(this._annotations[projectUuid][uuid], updates);
	}

	static unstub(stub: annotationStub, ownerUuid?: accountUuid): annotation {
		const fields: any = {
			uuid:         Uuid.base64(),
			account_uuid: ownerUuid ?? app.accountUuid,
			created_dt:   getDateString(),
			updated_dt:   null
		};

		if (ownerUuid && app.accountUuid && ownerUuid !== app.accountUuid)
			fields.created_by = app.accountUuid;

		return Object.assign(fields, stub);
	}

	async create(data: annotationStub, projectUuid: projectUuid, topicUuid: topicUuid | undefined = undefined, prepend = false, ctx?: any): Promise<annotation> {
		const ownerUuid = projectStore.byUuid(projectUuid)?.account_uuid;
		const annotation = prepareForCreate(AnnotationStore.unstub(data, ownerUuid)) as annotation;
		const uuid = annotation.uuid;

		if (topicUuid === undefined && annotation.type !== "bookmark")
			throw new Error("New annotations created without a topic can only be bookmarks");

		this._load(projectUuid, annotation);
		if (topicUuid)
			outlineStore._loadAnnotationIntoTopic(uuid, topicUuid, projectUuid, prepend);
		else
			outlineStore._loadAnnotationIntoSpecialTopic(uuid, "bookmarks", projectUuid, prepend);

		// noinspection ES6MissingAwait
		(async () => {
			try {
				const {rendered_citation} = await app.backend.createAnnotation(cleanUp(annotation) as annotation, projectUuid, topicUuid, prepend);

				runInAction(() => {
					if (this._annotations[projectUuid] && this._annotations[projectUuid][uuid]) {
						const annotation: annotation = cleanUp(this._annotations[projectUuid][uuid]) as annotation;

						if (rendered_citation && annotation.citation)
							annotation.citation.rendered_citation = rendered_citation;

						this._annotations[projectUuid][uuid] = annotation;
					}
				});
			} catch (e) {
				app.handleBackendError(e, ctx);

				// Rollback
				runInAction(() => {
					this._remove(uuid, projectUuid);
					outlineStore._removeAnnotation(uuid, projectUuid);
				});
			}
		})();

		return annotation;
	}

	async update(uuid: annotationUuid, projectUuid: projectUuid, updates: annotationUpdates, ctx?: any): Promise<annotation> {
		if (this._annotations[projectUuid]?.[uuid] === undefined)
			throw new Error(`Annotation not found: ${uuid} (project ${projectUuid})`);

		const original = cleanUp(this._annotations[projectUuid][uuid]) as annotation;
		const annotation = Object.assign(prepareForUpdate(this._annotations[projectUuid][uuid]) as annotation, updates);
		this._load(projectUuid, annotation);

		// noinspection ES6MissingAwait
		(async () => {
			try {
				await app.backend.updateAnnotation(uuid, updates);
				runInAction(() => {
					if (this._annotations[projectUuid] && this._annotations[projectUuid][uuid])
						this._annotations[projectUuid][uuid] = cleanUp(this._annotations[projectUuid][uuid]) as annotation;
				});
			} catch (e) {
				app.handleBackendError(e, ctx);
				this._load(projectUuid, original);
			}
		})();

		return annotation;
	}

	async delete(uuid: annotationUuid, projectUuid: projectUuid, ctx?: any) {
		if (this._annotations[projectUuid]?.[uuid] === undefined)
			throw new Error(`Annotation not found: ${uuid} (project ${projectUuid})`);

		const original = cleanUp(this._annotations[projectUuid][uuid]) as annotation;
		const originalOutlineData = outlineStore._removeAnnotation(uuid, projectUuid);
		this._remove(uuid, projectUuid);

		// noinspection ES6MissingAwait
		(async () => {
			try {
				await app.backend.deleteAnnotation(uuid);
			} catch (e) {
				app.handleBackendError(e, ctx);
				runInAction(() => {
					this._load(projectUuid, original);
					if (originalOutlineData)
						outlineStore._loadAnnotationIntoOutlineTopicUsingPosition(uuid, originalOutlineData.outlineTopic, originalOutlineData.position);
				});
			}
		})();
	}

	/**
	 * Moves the annotation to the end of the given topic within the same outline.
	 * Returns the new position.
	 */
	async moveToTopic(uuid: annotationUuid, projectUuid: projectUuid, topicUuid: topicUuid, ctx?: any): Promise<number> {
		await outlineStore._fetch(projectUuid); // make sure the outline is loaded (see "unknown error" code below)

		let originalOutlineData: {outlineTopic: outlineTopic; position: number} | null;
		let newPosition: number | null = null;

		runInAction(() => {
			originalOutlineData = outlineStore._removeAnnotation(uuid, projectUuid);
			newPosition = outlineStore._loadAnnotationIntoTopic(uuid, topicUuid, projectUuid, false);
		});

		if (newPosition === null) // this would mean the project isn't loaded, but we ensured it is at the top of this function
			throw new Error("Unknown error");

		// noinspection ES6MissingAwait
		(async () => {
			try {
				await app.backend.changeAnnotationTopic(uuid, topicUuid, projectUuid);
			} catch (e) {
				app.handleBackendError(e, ctx);

				// @ts-ignore - yes, TS, this is fine
				if (originalOutlineData) {
					runInAction(() => {
						outlineStore._removeAnnotation(uuid, projectUuid);
						// @ts-ignore - same as a few lines above
						outlineStore._loadAnnotationIntoOutlineTopicUsingPosition(uuid, originalOutlineData.outlineTopic, originalOutlineData.position);
					});
				}
			}
		})();

		return newPosition;
	}

	/**
	 * Removes the given annotation, and also removes it from outlines
	 */
	_remove(uuid: annotationUuid, projectUuid?: projectUuid) {
		if (projectUuid === undefined) {
			(Object.keys(this._annotations) as projectUuid[])
				.filter(projectUuid => this._isProjectLoaded(projectUuid) && this._annotations[projectUuid][uuid] !== undefined)
				.forEach(projectUuid => delete this._annotations[projectUuid][uuid]);
		} else if (this._isProjectLoaded(projectUuid) && this._annotations[projectUuid][uuid] !== undefined)
			delete this._annotations[projectUuid][uuid];

		outlineStore._removeAnnotation(uuid, projectUuid);
	}

	_setupSyncHandlers(sync: Sync) {
		sync.setHandler(syncHandler.ANNOTATION_ADD, this._loadFromSync.bind(this));
		sync.setHandler(syncHandler.ANNOTATIONS_ADD, this._loadMultipleFromSync.bind(this));
		sync.setHandler(syncHandler.ANNOTATION_MODIFY, (data: syncData[syncHandler.ANNOTATION_MODIFY]) => {
			if (this._isProjectLoaded(data.project_uuid))
				this._loadUpdates(data.annotation.uuid, data.project_uuid, data.annotation);
		});
		sync.setHandler(syncHandler.ANNOTATION_DELETE, ({annotation_uuid, project_uuid}: syncData[syncHandler.ANNOTATION_DELETE]) =>
			this._remove(annotation_uuid, project_uuid)
		);
	}

	_loadFromSync(data: syncData[syncHandler.ANNOTATION_ADD]) {
		if (this._isProjectLoaded(data.project_uuid))
			this._load(data.project_uuid, data.annotation);

		if (data.topic_uuid)
			outlineStore._loadAnnotationIntoTopic(data.annotation.uuid, data.topic_uuid, data.project_uuid, !!data.prepend);
		else if (data.topic_type)
			outlineStore._loadAnnotationIntoSpecialTopic(data.annotation.uuid, data.topic_type, data.project_uuid, !!data.prepend);
	}

	_loadMultipleFromSync(data: syncData[syncHandler.ANNOTATIONS_ADD]) {
		data.annotations.forEach(this._loadFromSync.bind(this));
	}

	_setupOutSyncHandlers(handler: outSyncHandler) {
		reaction(() => JSON.parse(JSON.stringify(this._annotations)), (current: dataModel, previous: dataModel) => {
			if (!app.isLoggedIn)
				return;

			const addMsgs: Record<projectUuid, syncData[syncHandler.ANNOTATIONS_ADD]> = {};
			(Object.keys(current) as projectUuid[])
				.forEach(projectUuid => (Object.keys(current[projectUuid]) as annotationUuid[])
					.filter(uuid => previous[projectUuid]?.[uuid] === undefined)
					.forEach(uuid => {
						const msg: syncData[syncHandler.ANNOTATION_ADD] = {
							project_uuid: projectUuid,
							annotation:   current[projectUuid][uuid]
						};

						const ot = outlineStore.getOutlineTopicForAnnotation(uuid, projectUuid);
						if (!ot?.annotations.find(a => a.annotation_uuid === uuid)) {
							console.warn("PN Data: Annotation added not found in the outline", {
								annotationUuid: uuid,
								projectUuid
							});
							return;
						}

						msg.prepend = (ot.annotations[0].annotation_uuid === uuid);
						if ("topic_uuid" in ot)
							msg.topic_uuid = ot.topic_uuid;
						else
							msg.topic_type = ot.type;

						if (addMsgs[projectUuid] === undefined)
							addMsgs[projectUuid] = {annotations: []};
						addMsgs[projectUuid].annotations.push(msg);
					})
				);
			(Object.keys(addMsgs) as projectUuid[])
				.forEach(projectUuid => handler(projectUuid, syncHandler.ANNOTATIONS_ADD, addMsgs[projectUuid]));

			(Object.keys(previous) as projectUuid[])
				.forEach(projectUuid => (Object.keys(previous[projectUuid]) as annotationUuid[])
					.filter(uuid => current[projectUuid]?.[uuid] === undefined)
					.forEach(uuid => handler(projectUuid, syncHandler.ANNOTATION_DELETE, {
						project_uuid:    projectUuid,
						annotation_uuid: uuid
					}))
				);

			(Object.keys(current) as projectUuid[])
				.filter(projectUuid => previous[projectUuid] !== undefined)
				.forEach(projectUuid => (Object.keys(current[projectUuid]) as annotationUuid[])
					.filter(uuid => previous[projectUuid][uuid] !== undefined
						&& !comparer.structural(current[projectUuid][uuid], previous[projectUuid][uuid]))
					.forEach(uuid => handler(projectUuid, syncHandler.ANNOTATION_MODIFY, {
						project_uuid: projectUuid,
						annotation:   current[projectUuid][uuid]
					}))
				);
		});
	}
}

export default new AnnotationStore();
